import Query from './Query';
import Orm from './Orm';

export default class Model {
    /**
     * The namespace used in store
     */
    static get namespace() {
        return Orm.namespace;
    }

    /**
     * The primary key to be used for the model.
     */
    static get primaryKey() {
        return 'id';
    }

    /**
     * Create a new model instance.
     */
    constructor(record) {
        this.$fill(record);
    }

    /**
     * The definition of the fields of the model and its relations.
     */
    static fields() {
        return {};
    }

    /**
     * Create an attr attribute.
     */
    static attr(value, mutator = null) {
        return {
            type: 'attribute',
            default: value,
            mutator: mutator
        };
    }

    /**
     * Create belongsTo relation.
     */
    static belongsTo(related, foreignKey) {
        return {
            type: 'belongsTo',
            related: related,
            foreignKey: foreignKey
        };
    }

    /**
     * Create hasMany relation.
     */
    static hasMany(related, foreignKey) {
        return {
            type: 'hasMany',
            related: related,
            foreignKey: foreignKey
        };
    }

    /**
     * Create hasOne relation.
     */
    static hasOne(related, foreignKey) {
        return {
            type: 'hasOne',
            related: related,
            foreignKey: foreignKey
        };
    }

    /**
     * Fill the model instance with the given record. If no record were passed,
     * or if the record has any missing fields, each value of the fields will
     * be filled with its default value defined at model fields definition.
     */
    $fill(record = {}) {
        const fields = this.$self().fields();

        for (const key in fields) {
            let value = fields[key].default;

            if(fields[key].type === 'attribute') {
                if (key in record) {
                    value = record[key];
                }

                if (fields[key].mutator) {
                    value = fields[key].mutator(value);
                }

                this[key] = value;
            }
        }
    }

    /**
     * Load Relations
     */
    $load(localKeys) {
        const keys = Array.isArray(localKeys) ? localKeys : [localKeys]

        keys.forEach(localKey => {
            const firstKey = localKey.split('.')[0]
            const remainingKeys = localKey.split('.').splice(1)

            const relation = this.$self().relations().find(r => r.localKey === firstKey)

            if(!relation) {
                return
            }

            if(relation.type === 'belongsTo') {
                const relatedModel = relation.related.all().find(model => model.id == this[relation.foreignKey])
                if(remainingKeys.length && relatedModel) {
                    relatedModel.$load(remainingKeys.join('.'))
                }
                this[firstKey] = relatedModel
            } else if(relation.type === 'hasMany') {
                let relatedModels = relation.related.all().filter(model => this.id == model[relation.foreignKey])
                if(remainingKeys.length) {
                    relatedModels = relatedModels.map(r => r.$load(remainingKeys.join('.')))
                }
                this[firstKey] = relatedModels
            } else if(relation.type === 'hasOne') {
                let relatedModel = relation.related.all().find(model => this.id == model[relation.foreignKey])
                if(remainingKeys.length) {
                    relatedModel = relatedModel.$load(remainingKeys.join('.'))
                }
                this[firstKey] = relatedModel
            }
        })

        return this
    }

    /**
     * Load All Relations
     */
    $loadAll() {
        const relations = this.$self().relations()
        return this.$load(relations.map(r => r.localKey))
    }

    /**
     * Get Model Relations
     */
    static relations() {
        const relations = []
        const fields = this.fields();
        for (const key in fields) {
            if(fields[key].type !== 'attribute') {
                relations.push({ ...fields[key], localKey: key })
            }
        }

        return relations
    }

    /**
     * Get the store instance from the container.
     */
    static store() {
        return Orm.store;
    }

    /**
     * Call Vuex Getters.
     */
    static getters(method) {
        return this.store().getters[`${this.namespace}/${method}`];
    }

    /**
     * Get the constructor of this model.
     */
    $self() {
        return this.constructor;
    }

    /**
     * Get the store instance from the container.
     */
    $store() {
        return this.$self().store();
    }

    /**
     * Get all records.
     */
    static all() {
        return this.getters('all')(this.entity);
    }

    /**
     * Find a record.
     */
    static find(id) {
        return this.getters('find')(this.entity, this.primaryKey, id);
    }

    /**
     * Get query instance.
     */
    static query() {
        return new Query(this, this.store())
    }

    /**
     * Create records initially.
     */
    static init(payload) {
        const data = payload.freeze ? Object.freeze(payload.data) : payload.data
        this.store().commit(`${this.namespace}/create`, { model: this, data: data, init: true })
    }

    /**
     * Create records.
     */
    static create(payload) {
        const data = payload.freeze ? Object.freeze(payload.data) : payload.data
        this.store().commit(`${this.namespace}/create`, { model: this, data: data })
    }

    /**
     * Insert records.
     */
    static insert(payload) {
        const data = payload.freeze ? Object.freeze(payload.data) : payload.data
        this.store().commit(`${this.namespace}/insert`, { model: this, data: data })
    }

    /**
     * Delete all records.
     */
    static deleteAll() {
        this.create({ data: [] })
    }

    /**
     * Update records.
     */
    static update(data, freeze = false) {
        let dataToUpdate = Array.isArray(data) ? data : [data]
        dataToUpdate = freeze ? Object.freeze(dataToUpdate) : dataToUpdate
        this.store().commit(`${this.namespace}/update`, { model: this, data: dataToUpdate })
    }

    /**
     * Save record.
     */
    $save() {
        this.$store().commit(`${this.$self().namespace}/insert`, { model: this.$self(), data: [this.$toJson()] })
    }

    /**
     * Delete record.
     */
    $delete() {
        this.$store().commit(`${this.$self().namespace}/delete`, { model: this.$self(), data: this.$toJson() })
    }

    /**
     * Serialize field values into json.
     */
    $toJson(includeRelations = true) {
        let record = {}
        for(const key in this) {
            const field = this.$self().fields()[key]

            if(!field) {
                continue
            }

            if(field.type === 'attribute') {
                record[key] = this[key]
            } else if(includeRelations && this[key]) {
                record[key] = this[key]
            }
        }

        return record;
    }
}
